import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDa from '@angular/common/locales/da';
import localeEn from '@angular/common/locales/en';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';

import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';

import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { environment } from '@env/environment';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { BootstrapEffects } from '@app/main-store/bootstrap.effects';
import { SubscriptionEffects } from '@app/main-store/subscriptions/subscription.effects';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES } from '@config';
import { provideTransloco } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { primengConfig } from '../theme/primeng.config';
import { AuthErrorInterceptor } from './_interceptors/auth-error.interceptor';
import { UrlInterceptor } from './_interceptors/url.interceptor';
import provideFontAwesome from './_shared/material/font-awesome.provider';
import { LanguageInitializationService } from './_shared/service/language-initialization.service';
import { routes } from './app.routes';
import { CustomErrorHandler } from './global-error-handler';
import { FeatureFlagsEffects } from './main-store/feature-flags.effects';
import { IntegrationsEffects } from './main-store/integrations/integrations.effects';
import { reducers } from './main-store/root-reducer';
import { TranslocoHttpLoader } from './transloco-loader';
// Get the initialized language
const initialLanguage = LanguageInitializationService.initializeLanguage();

// Use same logic as in module
const skipRedirectCallbacksFor = ['/integrations/shopify', '/integrations/stripe', '/oauth/install'];

registerLocaleData(localeDa);
registerLocaleData(localeEn);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    ...provideFontAwesome,
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),

    // Auth0
    importProvidersFrom(
      AuthModule.forRoot({
        domain: environment.auth0Domain,
        clientId: environment.auth0ClientId,
        authorizationParams: {
          audience: environment.auth0Audience,
          redirect_uri: window.location.origin + '/auth',
        },
        httpInterceptor: {
          allowedList: [environment.apiUrl + '/*', environment.apiLaravelUrl + '/*'],
        },
        skipRedirectCallback: skipRedirectCallbacksFor.includes(window.location.pathname),
        cacheLocation: 'localstorage',
      }),
    ),

    // NGRX
    provideStore(reducers),
    provideEffects([BootstrapEffects, FeatureFlagsEffects, SubscriptionEffects, IntegrationsEffects]),
    provideRouterStore(),
    ...(!environment.production ? [provideStoreDevtools({ maxAge: 50 })] : []),

    // Transloco
    provideTransloco({
      config: {
        availableLangs: SUPPORTED_LANGUAGE_CODES,
        defaultLang: initialLanguage,
        fallbackLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: {
          logMissingKey: !environment.production,
          useFallbackTranslation: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),

    // PrimeNG
    providePrimeNG(primengConfig),
    DialogService,
    MessageService,

    // Custom Providers
    DatePipe,
    { provide: HTTP_INTERCEPTORS, useClass: UrlInterceptor, multi: true },
    // Important, order matters: AuthErrorInterceptor must be declared before AuthHttpInterceptor to properly handle errors
    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: Window, useValue: window },
  ],
};
